/**
 * Spotify.tsx
 *
 * Displays a "Currently Playing" Spotify card if the user is listening
 * to a track. Uses SWR to poll the `/api/spotify` endpoint and shows:
 *
 * - Album art
 * - Song title
 * - Artist name
 * - Spotify icon
 *
 * Wrapped in a Tooltip showing "Currently playing on my Spotify".
 *
 * Requirements:
 * - Next.js 13+
 * - SWR
 * - New Tooltipfunc implementation (Floating UI based)
 */

import clsx from "clsx";
import * as React from "react";
import { SiSpotify } from "react-icons/si";
import useSWR from "swr";

import Tooltipfunc from "components/Tooltip";
import NextImage from "components/NextImage";
import UnstyledLink, { UnstyledLinkProps } from "components/UnstyledLink";

import { SpotifyData } from "types/spotify";

export interface SpotifyProps
  extends Omit<UnstyledLinkProps, "href" | "children"> {
  className?: string;
}

/**
 * Fetches Spotify "currently playing" data and renders a tooltip-wrapped card.
 */
export default function Spotify({ className, ...rest }: SpotifyProps) {
  /**
   * Fetch data from /api/spotify using SWR.
   * Expected shape defined in `types/spotify.ts`.
   */
  const { data } = useSWR<SpotifyData>("/api/spotify");

  /** If no track is playing, render nothing. */
  if (!data?.isPlaying) return null;

  return (
    <figure className={className} data-cy="spotify">
      {/* Tooltip wrapper */}
      <Tooltipfunc
        trigger="mouseenter"
        interactive
        tipChildren={
          <div className="inline-block rounded-md border bg-white p-2 text-gray-600 shadow-md dark:border-gray-600 dark:bg-dark dark:text-gray-200">
            <p>Currently playing on my Spotify</p>
          </div>
        }
      >
        {/* Clickable Spotify card */}
        <UnstyledLink
          {...rest}
          href={data.songUrl}
          className={clsx(
            "relative flex items-center gap-4 p-3",
            "border dark:border-gray-600",
            "border-thin w-72 rounded-md",
            "shadow-sm dark:shadow-none",
            "focus:outline-none focus-visible:ring focus-visible:ring-primary-300"
          )}
        >
          {/* Album Art */}
          <NextImage
            useSkeleton
            className="w-16 shadow-sm dark:shadow-none"
            src={data.albumImageUrl}
            alt={data.album}
            width={640}
            height={640}
            unoptimized
          />

          {/* Song Details */}
          <div className="flex-1">
            <p className="text-sm font-medium">{data.title}</p>
            <p className="mt-1 text-xs text-gray-600 dark:text-gray-300">
              {data.artist}
            </p>
          </div>

          {/* Spotify Icon */}
          <div className="absolute bottom-1.5 right-1.5">
            <SiSpotify size={25} color="#1ED760" />
          </div>
        </UnstyledLink>
      </Tooltipfunc>
    </figure>
  );
}
